import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Tooltip, Chip, IconButton, Stack, Grid,
  CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControlLabel, Checkbox, Card, CardContent, Divider
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon, FilterList as FilterIcon,
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  MeetingRoom as RoomIcon, AccessTime as TimeIcon, Groups as GroupsIcon,
  Person as TeacherIcon, Refresh as RefreshIcon, EventRepeat as RepeatIcon,
  CheckCircle as CheckCircleIcon, Close as CloseIcon
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import GenericPage from '../../Commun/components/GenericPage';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useAuth } from '../../Commun/contexts/AuthContext';
import { academyStore } from '../services/academyStore';
import { planningTheoriqueApi } from '../services/planningTheoriqueApi';
import {
  PlanningTheorique,
  PlanningTheoriqueVac,
  PlanningTheoriqueMatrixRow,
  PlanningMatrixFilters
} from '../types/planningTheorique';

const DAYS_NAMES = ['DIM', 'LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM'];

const formatToday = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatNextYear = (): string => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const TheoreticalPlanningMatrixPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useLanguage();
  const { user } = useAuth();
  const [, setTick] = useState(0);

  const rawAgency = user?.agency;
  const parsedDosId = rawAgency ? parseInt(String(rawAgency), 10) : 1;
  const connectedDosId = (!isNaN(parsedDosId) && parsedDosId > 0) ? parsedDosId : 1;

  // ── Filters Dialog State (ouvert par défaut à l'arrivée sur la page) ──
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [filterStartDate, setFilterStartDate] = useState<string>(formatToday());
  const [filterEndDate, setFilterEndDate] = useState<string>(formatNextYear());
  const [selectedRoomIds, setSelectedRoomIds] = useState<number[]>([]);

  // ── Data & Matrix State ──
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [matrixRows, setMatrixRows] = useState<PlanningTheoriqueMatrixRow[]>([]);

  // ── Modal Planification de Vac ──
  const [vacModalOpen, setVacModalOpen] = useState(false);
  const [isEditingVac, setIsEditingVac] = useState(false);
  const [activeTheo, setActiveTheo] = useState<PlanningTheorique | null>(null);
  const [vacFormData, setVacFormData] = useState<Partial<PlanningTheoriqueVac>>({
    start_time: '08:00',
    end_time: '09:30',
    day_num: 1,
    grp_idf: null,
    tch_idf: null
  });

  useEffect(() => {
    academyStore.ensureRoomsLoaded();
    academyStore.ensureGroupsLoaded();
    academyStore.ensureTeachersLoaded();
    return academyStore.subscribe(() => setTick(tick => tick + 1));
  }, []);

  const rooms = academyStore.getRooms();
  const groups = academyStore.getGroups();
  const teachers = academyStore.getTeachers();

  // Initialiser la sélection de toutes les salles dès qu'elles sont disponibles
  useEffect(() => {
    if (rooms.length > 0 && selectedRoomIds.length === 0) {
      setSelectedRoomIds(rooms.map(r => r.id));
    }
  }, [rooms]);

  // ── Chargement des données de la matrice ──
  const handleLoadMatrix = async () => {
    setLoading(true);
    try {
      // 1. Récupérer tous les plannings théoriques
      const allPlannings = await planningTheoriqueApi.getPlanningTheoriques();

      // 2. Filtrer selon les salles et les dates sélectionnées
      const filteredPlannings = allPlannings.filter(p => {
        // Filtre salle
        if (selectedRoomIds.length > 0 && !selectedRoomIds.includes(p.arm_idf)) {
          return false;
        }
        // Filtre dates (recouvrement)
        if (filterStartDate && p.end_date && p.end_date < filterStartDate) {
          return false;
        }
        if (filterEndDate && p.start_date > filterEndDate) {
          return false;
        }
        return true;
      });

      // 3. Charger tous les créneaux (vacs)
      const allVacs = await planningTheoriqueApi.getPlanningTheoriqueVacs();

      // 4. Construire les lignes de la matrice
      const rows: PlanningTheoriqueMatrixRow[] = filteredPlannings.map(theo => {
        const theoVacs = allVacs.filter(v => v.theo_idf === theo.id);
        return {
          theo,
          vacs: theoVacs
        };
      });

      setMatrixRows(rows);
      setLoaded(true);
      setFiltersOpen(false);
    } catch (err: any) {
      blobticMessage.error(t('commun.error'), err?.message || 'Erreur lors du chargement de la matrice.');
    } finally {
      setLoading(false);
    }
  };

  // ── Sélection des salles dans la popup de filtres ──
  const handleToggleRoom = (roomId: number) => {
    setSelectedRoomIds(prev =>
      prev.includes(roomId) ? prev.filter(id => id !== roomId) : [...prev, roomId]
    );
  };

  const handleSelectAllRooms = () => {
    if (selectedRoomIds.length === rooms.length) {
      setSelectedRoomIds([]);
    } else {
      setSelectedRoomIds(rooms.map(r => r.id));
    }
  };

  // ── Calcul du nombre max de semaines parmi les plannings chargés ──
  const maxWeeks = matrixRows.reduce((max, r) => Math.max(max, r.theo.nbr_week), 1);

  // ── Gestion des créneaux (Vacs) ──
  const handleOpenAddVac = (theo: PlanningTheorique, dayNum: number) => {
    setActiveTheo(theo);
    setVacFormData({
      theo_idf: theo.id,
      dos_idf: theo.dos_idf || connectedDosId,
      arm_idf: theo.arm_idf,
      day_num: dayNum,
      start_time: '08:00',
      end_time: '09:30',
      grp_idf: groups.length > 0 ? groups[0].id : null,
      tch_idf: teachers.length > 0 ? teachers[0].id : null
    });
    setIsEditingVac(false);
    setVacModalOpen(true);
  };

  const handleOpenEditVac = (theo: PlanningTheorique, vac: PlanningTheoriqueVac) => {
    setActiveTheo(theo);
    setVacFormData({ ...vac });
    setIsEditingVac(true);
    setVacModalOpen(true);
  };

  const handleSaveVac = async () => {
    if (!vacFormData.theo_idf || !activeTheo) return;
    if (!vacFormData.start_time || !vacFormData.end_time) {
      blobticMessage.warning(t('commun.warning'), t('academy.planning_theorique.validation.hours_required'));
      return;
    }
    if (vacFormData.start_time >= vacFormData.end_time) {
      blobticMessage.warning(t('commun.warning'), t('academy.planning_theorique.validation.invalid_time_range'));
      return;
    }

    try {
      const payload: Partial<PlanningTheoriqueVac> = {
        ...vacFormData,
        arm_idf: activeTheo.arm_idf,
        dos_idf: activeTheo.dos_idf || connectedDosId
      };

      if (isEditingVac && vacFormData.id) {
        const res = await planningTheoriqueApi.updatePlanningTheoriqueVac(payload);
        if (res && res.success === false) {
          blobticMessage.error(t('commun.error'), res.message || res.error || 'Erreur lors de la modification.');
          return;
        }
        blobticMessage.success(t('commun.success'), t('academy.planning_theorique.msg_vac_updated'));
      } else {
        const res = await planningTheoriqueApi.insertPlanningTheoriqueVac(payload);
        if (res && res.success === false) {
          blobticMessage.error(t('commun.error'), res.message || res.error || 'Erreur lors de l\'ajout.');
          return;
        }
        blobticMessage.success(t('commun.success'), t('academy.planning_theorique.msg_vac_created'));
      }

      setVacModalOpen(false);
      await handleLoadMatrix();
    } catch (err: any) {
      blobticMessage.error(t('commun.error'), err?.message || 'Erreur lors de l\'enregistrement.');
    }
  };

  const handleDeleteVac = async (vacId: number) => {
    const confirmed = await blobticMessage.confirm({
      title: t('commun.confirmation'),
      message: t('academy.planning_theorique.slot_confirm_delete') || 'Supprimer ce créneau théorique ?',
      severity: 'warning'
    });
    if (!confirmed) return;

    try {
      const res = await planningTheoriqueApi.deletePlanningTheoriqueVac(vacId);
      if (res && res.success === false) {
        blobticMessage.error(t('commun.error'), res.message || res.error || 'Erreur de suppression.');
        return;
      }
      blobticMessage.success(t('commun.success'), t('academy.planning_theorique.msg_vac_deleted'));
      await handleLoadMatrix();
    } catch (err: any) {
      blobticMessage.error(t('commun.error'), err?.message || 'Erreur lors de la suppression.');
    }
  };

  return (
    <GenericPage
      title={t('academy.planning_theorique.matrix_title')}
      icon={<CalendarIcon />}
      footerEnabled={false}
      additionalActions={
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setFiltersOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            {t('academy.planning_theorique.filter_btn')}
          </Button>

          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            disabled={loading}
            onClick={handleLoadMatrix}
            sx={{ borderRadius: 2 }}
          >
            {t('academy.planning_theorique.refresh')}
          </Button>
        </Stack>
      }
    >
      <Box sx={{ pb: 4 }}>
        {/* ── Dialog Filtres (affiché automatiquement à l'ouverture) ── */}
        <Dialog
          open={filtersOpen}
          onClose={() => { if (loaded) setFiltersOpen(false); }}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <FilterIcon color="primary" />
              <Typography variant="h6" fontWeight={700}>
                {t('academy.planning_theorique.filter_dialog_title')}
              </Typography>
            </Stack>
            {loaded && (
              <IconButton size="small" onClick={() => setFiltersOpen(false)}>
                <CloseIcon />
              </IconButton>
            )}
          </DialogTitle>

          <DialogContent dividers sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('academy.planning_theorique.filter_dialog_desc')}
            </Typography>

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  fullWidth
                  label={t('academy.planning_theorique.start_date')}
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  fullWidth
                  label={t('academy.planning_theorique.end_date')}
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {t('academy.planning_theorique.filter_rooms')} ({selectedRoomIds.length}/{rooms.length})
                  </Typography>
                  <Button size="small" onClick={handleSelectAllRooms}>
                    {selectedRoomIds.length === rooms.length
                      ? t('academy.planning_theorique.deselect_all')
                      : t('academy.planning_theorique.select_all')}
                  </Button>
                </Stack>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    maxHeight: 220,
                    overflowY: 'auto',
                    borderRadius: 2,
                    bgcolor: isDark ? 'background.default' : alpha(theme.palette.grey[100], 0.7)
                  }}
                >
                  <Grid container spacing={1}>
                    {rooms.map((r) => {
                      const isChecked = selectedRoomIds.includes(r.id);
                      return (
                        <Grid item xs={12} sm={6} key={r.id}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={isChecked}
                                onChange={() => handleToggleRoom(r.id)}
                              />
                            }
                            label={
                              <Typography variant="body2">
                                <strong>{r.room_number}</strong> {r.floor ? `(${r.floor})` : ''}
                              </Typography>
                            }
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 2.5 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <FilterIcon />}
              onClick={handleLoadMatrix}
              disabled={loading}
              sx={{ borderRadius: 2, py: 1.2 }}
            >
              {t('academy.planning_theorique.load_btn')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── État non chargé ── */}
        {!loaded && !loading && (
          <Paper elevation={1} sx={{ p: 6, textAlign: 'center', borderRadius: 3, bgcolor: 'background.paper' }}>
            <CalendarIcon sx={{ fontSize: 52, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('academy.planning_theorique.not_loaded_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('academy.planning_theorique.not_loaded_desc')}
            </Typography>
            <Button variant="contained" startIcon={<FilterIcon />} onClick={() => setFiltersOpen(true)}>
              {t('academy.planning_theorique.open_filters')}
            </Button>
          </Paper>
        )}

        {/* ── Matrice du Planning Théorique ── */}
        {loaded && (
          <Paper
            elevation={2}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: 'background.paper',
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <TableContainer sx={{ maxHeight: 'calc(100vh - 240px)' }}>
              <Table stickyHeader size="small" sx={{ borderCollapse: 'separate' }}>
                <TableHead>
                  {/* Ligne d'en-tête 1 : Colonnes fixes + Groupement par Semaine */}
                  <TableRow>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        fontWeight: 700,
                        minWidth: 160,
                        bgcolor: isDark ? 'grey.900' : 'grey.100',
                        borderRight: `2px solid ${theme.palette.divider}`,
                        position: 'sticky',
                        left: 0,
                        zIndex: 3
                      }}
                    >
                      {t('academy.planning_theorique.room')}
                    </TableCell>

                    <TableCell
                      rowSpan={2}
                      align="center"
                      sx={{
                        fontWeight: 700,
                        minWidth: 100,
                        bgcolor: isDark ? 'grey.900' : 'grey.100',
                        borderRight: `1px solid ${theme.palette.divider}`
                      }}
                    >
                      {t('academy.planning_theorique.nbr_weeks')}
                    </TableCell>

                    <TableCell
                      rowSpan={2}
                      align="center"
                      sx={{
                        fontWeight: 700,
                        minWidth: 180,
                        bgcolor: isDark ? 'grey.900' : 'grey.100',
                        borderRight: `2px solid ${theme.palette.divider}`
                      }}
                    >
                      {t('academy.planning_theorique.period')}
                    </TableCell>

                    {/* Semaines dynamiques en en-tête groupé */}
                    {Array.from({ length: maxWeeks }).map((_, wIdx) => (
                      <TableCell
                        key={`week-head-${wIdx}`}
                        colSpan={7}
                        align="center"
                        sx={{
                          fontWeight: 800,
                          fontSize: '0.85rem',
                          bgcolor: alpha(theme.palette.primary.main, isDark ? 0.25 : 0.1),
                          color: 'primary.main',
                          borderRight: `2px solid ${theme.palette.primary.main}`,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          py: 1
                        }}
                      >
                        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                          <RepeatIcon fontSize="small" />
                          <span>{t('academy.planning_theorique.week')} {wIdx + 1}</span>
                        </Stack>
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Ligne d'en-tête 2 : Les 7 jours pour chaque semaine */}
                  <TableRow>
                    {Array.from({ length: maxWeeks }).map((_, wIdx) =>
                      DAYS_NAMES.map((dayName, dIdx) => (
                        <TableCell
                          key={`day-head-${wIdx}-${dIdx}`}
                          align="center"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            minWidth: 130,
                            py: 0.8,
                            bgcolor: isDark ? 'grey.800' : 'grey.50',
                            borderRight: (dIdx === 6)
                              ? `2px solid ${theme.palette.primary.main}`
                              : `1px solid ${theme.palette.divider}`
                          }}
                        >
                          {dayName}
                        </TableCell>
                      ))
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {matrixRows.map((row) => {
                    const { theo, vacs } = row;
                    const isHoliday = theo.day_type === 'Holiday';

                    return (
                      <TableRow key={theo.id} hover>
                        {/* Colonne 1 : Salle (sticky) */}
                        <TableCell
                          sx={{
                            bgcolor: isDark ? 'background.paper' : '#fff',
                            borderRight: `2px solid ${theme.palette.divider}`,
                            position: 'sticky',
                            left: 0,
                            zIndex: 1
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center">
                            <RoomIcon fontSize="small" color="primary" />
                            <Box>
                              <Typography variant="body2" fontWeight={700}>
                                {theo.room_number || `Salle #${theo.arm_idf}`}
                              </Typography>
                              {theo.room_lib && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {theo.room_lib}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* Colonne 2 : Nb Semaines */}
                        <TableCell align="center" sx={{ borderRight: `1px solid ${theme.palette.divider}` }}>
                          <Chip
                            label={`${theo.nbr_week} sem.`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        </TableCell>

                        {/* Colonne 3 : Période & type */}
                        <TableCell align="center" sx={{ borderRight: `2px solid ${theme.palette.divider}` }}>
                          <Typography variant="caption" fontWeight={600} display="block">
                            {theo.start_date} → {theo.end_date || t('academy.planning_theorique.indefinite')}
                          </Typography>
                          <Chip
                            label={isHoliday ? t('academy.planning_theorique.type_holiday') : t('academy.planning_theorique.type_normal')}
                            size="small"
                            color={isHoliday ? 'warning' : 'success'}
                            sx={{ fontSize: '0.65rem', height: 20, mt: 0.5 }}
                          />
                        </TableCell>

                        {/* Colonnes de Jours pour chaque Semaine */}
                        {Array.from({ length: maxWeeks }).map((_, wIdx) => {
                          const isWeekActive = (wIdx + 1) <= theo.nbr_week;

                          return DAYS_NAMES.map((_, dIdx) => {
                            // Calcul du day_num correspondant (1-indexed)
                            // Semaine 1 : 1..7 (DIM à SAM)
                            // Semaine 2 : 8..14
                            const cellDayNum = (wIdx * 7) + (dIdx + 1);

                            if (!isWeekActive) {
                              return (
                                <TableCell
                                  key={`cell-${theo.id}-${cellDayNum}`}
                                  sx={{
                                    bgcolor: isDark ? alpha('#000', 0.2) : alpha(theme.palette.grey[200], 0.4),
                                    borderRight: (dIdx === 6)
                                      ? `2px solid ${theme.palette.primary.main}`
                                      : `1px solid ${theme.palette.divider}`,
                                    textAlign: 'center'
                                  }}
                                >
                                  <Typography variant="caption" color="text.disabled">—</Typography>
                                </TableCell>
                              );
                            }

                            // Créneaux pour ce day_num
                            const cellVacs = vacs.filter(v => v.day_num === cellDayNum);

                            return (
                              <TableCell
                                key={`cell-${theo.id}-${cellDayNum}`}
                                sx={{
                                  verticalAlign: 'top',
                                  p: 1,
                                  minWidth: 130,
                                  borderRight: (dIdx === 6)
                                    ? `2px solid ${theme.palette.primary.main}`
                                    : `1px solid ${theme.palette.divider}`,
                                  bgcolor: cellVacs.length > 0
                                    ? alpha(theme.palette.primary.main, isDark ? 0.08 : 0.03)
                                    : 'inherit'
                                }}
                              >
                                <Stack spacing={1}>
                                  {cellVacs.map((vac) => (
                                    <Card
                                      key={vac.id}
                                      variant="outlined"
                                      sx={{
                                        borderRadius: 2,
                                        bgcolor: isDark ? 'grey.900' : '#fff',
                                        borderColor: alpha(theme.palette.primary.main, 0.4),
                                        boxShadow: 1
                                      }}
                                    >
                                      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                        {/* Heures (heures seules visibles !) */}
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                          <TimeIcon sx={{ fontSize: 13, color: 'primary.main' }} />
                                          <Typography variant="caption" fontWeight={700} color="primary.main">
                                            {vac.start_time} - {vac.end_time}
                                          </Typography>
                                        </Stack>

                                        {/* Groupe */}
                                        {vac.grp_name && (
                                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                                            <GroupsIcon sx={{ fontSize: 13, color: 'secondary.main' }} />
                                            <Typography variant="caption" noWrap sx={{ maxWidth: 100 }}>
                                              {vac.grp_name}
                                            </Typography>
                                          </Stack>
                                        )}

                                        {/* Enseignant */}
                                        {vac.teacher_name && (
                                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.2 }}>
                                            <TeacherIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 100 }}>
                                              {vac.teacher_name}
                                            </Typography>
                                          </Stack>
                                        )}

                                        {/* Actions */}
                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ mt: 0.5 }}>
                                          <IconButton
                                            size="small"
                                            sx={{ p: 0.3 }}
                                            onClick={() => handleOpenEditVac(theo, vac)}
                                          >
                                            <EditIcon sx={{ fontSize: 13 }} />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            sx={{ p: 0.3 }}
                                            onClick={() => handleDeleteVac(vac.id)}
                                          >
                                            <DeleteIcon sx={{ fontSize: 13 }} />
                                          </IconButton>
                                        </Stack>
                                      </CardContent>
                                    </Card>
                                  ))}

                                  {/* Bouton Ajouter un créneau dans cette cellule */}
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                                    onClick={() => handleOpenAddVac(theo, cellDayNum)}
                                    sx={{
                                      border: `1px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
                                      borderRadius: 2,
                                      fontSize: '0.68rem',
                                      py: 0.3,
                                      textTransform: 'none',
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        borderColor: 'primary.main'
                                      }
                                    }}
                                  >
                                    + {t('academy.planning_theorique.add_vac')}
                                  </Button>
                                </Stack>
                              </TableCell>
                            );
                          });
                        })}
                      </TableRow>
                    );
                  })}

                  {matrixRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3 + maxWeeks * 7} align="center" sx={{ py: 6 }}>
                        <Typography variant="body1" color="text.secondary">
                          {t('academy.planning_theorique.no_matrix_data')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* ── Modal Planification / Modification d'une Vac ── */}
        <Dialog
          open={vacModalOpen}
          onClose={() => setVacModalOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle>
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarIcon color="primary" />
              <Typography variant="h6" fontWeight={700}>
                {isEditingVac
                  ? t('academy.planning_theorique.edit_vac_title')
                  : t('academy.planning_theorique.new_vac_title')}
              </Typography>
            </Stack>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 2.5 }}>
            <Stack spacing={2}>
              {/* Informations Salle et Jour */}
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('academy.planning_theorique.room')} : <strong>{activeTheo?.room_number || `Salle #${activeTheo?.arm_idf}`}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('academy.planning_theorique.day_num')} : <strong>Jour #{vacFormData.day_num}</strong> ({DAYS_NAMES[((vacFormData.day_num || 1) - 1) % 7]} - Semaine {Math.floor(((vacFormData.day_num || 1) - 1) / 7) + 1})
                </Typography>
              </Paper>

              {/* Heures (heures seules visibles) */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    type="time"
                    fullWidth
                    required
                    label={t('academy.planning_theorique.start_time')}
                    value={vacFormData.start_time || '08:00'}
                    onChange={(e) => setVacFormData({ ...vacFormData, start_time: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    type="time"
                    fullWidth
                    required
                    label={t('academy.planning_theorique.end_time')}
                    value={vacFormData.end_time || '09:30'}
                    onChange={(e) => setVacFormData({ ...vacFormData, end_time: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              {/* Sélection Groupe */}
              <TextField
                select
                fullWidth
                label={t('academy.planning_theorique.group')}
                value={vacFormData.grp_idf ?? ''}
                onChange={(e) => setVacFormData({ ...vacFormData, grp_idf: e.target.value ? Number(e.target.value) : null })}
              >
                <MenuItem value="">
                  <em>{t('academy.planning_theorique.none')}</em>
                </MenuItem>
                {groups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.name}
                  </MenuItem>
                ))}
              </TextField>

              {/* Sélection Enseignant */}
              <TextField
                select
                fullWidth
                label={t('academy.planning_theorique.teacher')}
                value={vacFormData.tch_idf ?? ''}
                onChange={(e) => setVacFormData({ ...vacFormData, tch_idf: e.target.value ? Number(e.target.value) : null })}
              >
                <MenuItem value="">
                  <em>{t('academy.planning_theorique.none')}</em>
                </MenuItem>
                {teachers.map((tch) => (
                  <MenuItem key={tch.id} value={tch.id}>
                    {tch.full_name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button variant="outlined" onClick={() => setVacModalOpen(false)}>
              {t('commun.cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckCircleIcon />}
              onClick={handleSaveVac}
            >
              {t('commun.save')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </GenericPage>
  );
};

export default TheoreticalPlanningMatrixPage;
